import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsLabelStateParentMixin from '../../mixins/ids-label-state-mixin/ids-label-state-parent-mixin';
import IdsDateAttributeMixin from '../../mixins/ids-date-attribute-mixin/ids-date-attribute-mixin';
import IdsMonthViewAttributeMixin from '../ids-month-view/ids-month-view-attribute-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsFieldIndicatorMixin from '../../mixins/ids-field-indicator-mixin/ids-field-indicator-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsValidationInputMixin from '../../mixins/ids-validation-mixin/ids-validation-input-mixin';
import IdsAutoSelectMixin from '../../mixins/ids-autoselect-mixin/ids-autoselect-mixin';
import IdsElement from '../../core/ids-element';

import { onPickerPopupXYSwitch } from '../ids-popup/ids-picker-popup-common';

import {
  buildClassAttrib,
  stringToBool,
  stringToNumber
} from '../../utils/ids-string-utils/ids-string-utils';
import {
  isValidDate, umalquraToGregorian, hoursTo24, parseLocalDate
} from '../../utils/ids-date-utils/ids-date-utils';
// Supporting components
// eslint-disable-next-line import/no-duplicates
import './ids-date-picker-popup';
// eslint-disable-next-line import/no-duplicates
import IdsDatePickerPopup from './ids-date-picker-popup';
import '../ids-button/ids-button';
import '../ids-toggle-button/ids-toggle-button';
import '../ids-icon/ids-icon';
import '../ids-text/ids-text';
import IdsTriggerField from '../ids-trigger-field/ids-trigger-field';

// Datepicker Shared
import {
  IdsDatePickerCommonAttributes,
} from './ids-date-picker-common';

// Types
import type {
  IdsRangeSettings,
  IdsDisableSettings,
  IdsLegend
} from '../ids-month-view/ids-month-view-common';
import type { IdsDayselectedEvent } from '../ids-month-view/ids-month-view';
import type IdsTimePicker from '../ids-time-picker/ids-time-picker';
import type IdsToggleButton from '../ids-toggle-button/ids-toggle-button';

// Styles
import styles from './ids-date-picker.scss';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { MARGIN_BLOCK_END_SIZES, MarginBlockEndKey } from '../ids-input/ids-input-attributes';

const Base = IdsAutoSelectMixin(
  IdsDirtyTrackerMixin(
    IdsFieldIndicatorMixin(
      IdsLabelStateParentMixin(
        IdsFieldHeightMixin(
          IdsColorVariantMixin(
            IdsValidationInputMixin(
              IdsMonthViewAttributeMixin(
                IdsDateAttributeMixin(
                  IdsLabelPositionMixin(
                    IdsLocaleMixin(
                      IdsKeyboardMixin(
                        IdsEventsMixin(
                          IdsElement
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Date Picker Component
 * @type {IdsDatePicker}
 * @inherits IdsElement
 * @mixes IdsColorVariantMixin
 * @mixes IdsDateAttributeMixin
 * @mixes IdsDirtyTrackerMixin
 * @mixes IdsEventsMixin
 * @mixes IdsFieldHeightMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsLabelStateParentMixin
 * @mixes IdsLabelPositionMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsMonthViewAttributeMixin
 * @mixes IdsAutoSelectMixin
 * @part container - the container of the component
 * @part trigger-field - the trigger container
 * @part trigger-button - the trigger button
 * @part icon - the icon in the trigger button
 * @part input - the input element
 * @part popup - the popup with calendar
 * @part footer - footer of the popup
 * @part btn-clear - the clear button in the calendar popup
 * @part btn-cancel - the cancel button in the calendar popup
 * @part btn-apply - the apply button in the calendar popup
 */
@customElement('ids-date-picker')
@scss(styles)
class IdsDatePicker extends Base {
  #lastValidRangeValue: string | null = '';

  #isInitializing = true;

  constructor() {
    super();

    this.picker = this.container?.querySelector<IdsDatePickerPopup>('ids-date-picker-popup');
    this.triggerButton = this.container?.querySelector('ids-trigger-button');
    this.triggerField = this.container?.querySelector('ids-trigger-field');
  }

  isFormComponent = true;

  picker?: IdsDatePickerPopup | null;

  triggerButton: any;

  triggerField: any;

  connectedCallback(): void {
    super.connectedCallback();
  }

  mountedCallback(): void {
    // Initialize DOM references
    this.picker = this.container?.querySelector<IdsDatePickerPopup>('ids-date-picker-popup');
    this.triggerButton = this.container?.querySelector('ids-trigger-button');
    this.triggerField = this.container?.querySelector('ids-trigger-field');

    // Apply tabbable setting if it was set before elements were initialized
    if (this.hasAttribute(attributes.TABBABLE)) {
      // Use the setter to ensure proper propagation to child elements
      this.tabbable = stringToBool(this.getAttribute(attributes.TABBABLE));
    }

    if (this.hasAttribute(attributes.OVERFLOW)) {
      this.overflow = this.getAttribute(attributes.OVERFLOW);
    }
    if (this.hasAttribute(attributes.TOOLTIP)) {
      this.tooltip = this.getAttribute(attributes.TOOLTIP);
    }
    if (this.hasAttribute(attributes.MARGIN_BLOCK_END)) {
      this.marginBlockEnd = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey;
    }

    this.#configurePickerPopup();
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();

    // Apply mask only if enabled
    if (this.mask) {
      this.#applyMask();
    }

    // Range-specific logic only for range pickers
    if (this.useRange) {
      this.#overrideUserEditHooks();
    }

    this.#validateButtons();
  }

  #validateButtons() {
    if (this.hasTime()) {
      this.showApply = this.hasTime();
    }

    this.#isInitializing = false;
  }

  /**
   * Override form input mixin hooks callbacks to handle user edits in date range mode
   * @private
   * @returns {void}
   */
  #overrideUserEditHooks() {
    if (!this.useRange) {
      return;
    }

    (this.triggerField as IdsTriggerField).onBeforeInput = () => {
      const originalMask = this.triggerField.mask;
      this.triggerField.mask = null;
      requestAnimationFrame(() => {
        this.triggerField.mask = originalMask;
      });
      return !this.validateRangeCurrentValue();
    };

    (this.triggerField as IdsTriggerField).onBeforeChange = () => {
      if (!this.validateRangeCurrentValue() && !this.validate) {
        const lastValidValue = this.getAttribute(attributes.VALUE);

        this.triggerField.input.value = lastValidValue;
        return true;
      }

      return false;
    };
  }

  /**
   * Apply range settings to the picker
   * @private
   */
  #applyRangeSettings(): void {
    if (this.useRange && this.value && this.picker) {
      const rangeParts = String(this.value).split(this.rangeSettings.separator);
      if (rangeParts.length === 2) {
        const [start, end] = rangeParts.map((value: string) => {
          const parsed = this.localeAPI.parseDate(value, { dateFormat: this.format });
          if (this.localeAPI.isIslamic() && Array.isArray(parsed) && parsed.length >= 3) {
            return umalquraToGregorian(parsed[0], parsed[1], parsed[2]);
          }
          return parsed as Date;
        });

        if (start && end) {
          const includeDisabled = this.rangeSettings.includeDisabled !== false;
          const collapseSameDates = this.rangeSettings.collapseSameDates;
          this.picker.rangeSettings = {
            ...this.picker.rangeSettings,
            start,
            end,
            includeDisabled,
            collapseSameDates
          };
        }
      }
    }
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      ...IdsDatePickerCommonAttributes,
      attributes.UTC_DATE_VALUE,
      attributes.DISABLED,
      attributes.ID,
      attributes.LABEL,
      attributes.LABEL_WRAP,
      attributes.MARGIN_BLOCK_END,
      attributes.MASK,
      attributes.NO_MARGINS,
      attributes.OVERFLOW,
      attributes.PLACEHOLDER,
      attributes.READONLY,
      attributes.SIZE,
      attributes.SHOW_TODAY,
      attributes.SHOW_APPLY,
      attributes.SHOW_CANCEL,
      attributes.SHOW_CLEAR,
      attributes.TABBABLE,
      attributes.TODAY_SHORTCUT,
      attributes.TOOLTIP,
      attributes.VALIDATE,
      attributes.VALIDATION_EVENTS,
      attributes.VALUE,
    ];
  }

  /**
   * List of available color variants for this component
   * @returns {Array<string>}
   */
  colorVariants = ['alternate-formatter', 'borderless', 'in-cell'];

  /**
   * Push color variant to the trigger-field element
   * @returns {void}
   */
  onColorVariantRefresh(): void {
    this.triggerField!.colorVariant = this.colorVariant;
  }

  /**
   * Push label-state to the trigger-field element
   * @returns {void}
   */
  onLabelRequiredChange(): void {
    this.triggerField!.labelRequired = this.labelRequired;
  }

  /**
   * Push label-state to the trigger-field element
   * @returns {void}
   */
  onLabelChange(): void {
    this.triggerField!.label = this.label;
  }

  /**
   * Push label-break to the container element
   * @returns {void}
   */
  onLabelBreakChange(): void {
    if (this.input) this.input.labelBreak = this.labelBreak;
  }

  /**
   * Push label-state to the trigger-field element
   * @returns {void}
   */
  onLabelStateChange(): void {
    if (this.triggerField) {
      this.triggerField.labelState = this.labelState;
    }
  }

  /**
   * Push label-position to the trigger-field element
   * @returns {void}
   */
  onLabelPositionChange(): void {
    this.triggerField!.labelPosition = this.labelPosition;
  }

  /**
   * Push suppress-validation to the trigger-field element
   * @param {boolean} val whether validation is suppressed
   */
  onSuppressValidationChange(val: boolean): void {
    if (this.triggerField) this.triggerField.suppressValidation = val;
  }

  /**
   * Push label-width to the trigger-field element
   * @returns {void}
   */
  onLabelWidthChange(): void {
    this.triggerField!.labelWidth = this.labelWidth;
  }

  /**
   * Push label-state to the trigger-field element
   * @returns {void}
   */
  onLabelAlignYChange(): void {
    this.triggerField!.labelAlignY = this.labelAlignY;
  }

  /**
   * Push label-alignment to the trigger-field element
   * @returns {void}
   */
  onLabelAlignmentChange(): void {
    this.triggerField!.labelAlignment = this.labelAlignment;
  }

  /**
   * Push label-wrap to the trigger-field element
   * @returns {void}
   */
  onLabelWrapChange(): void {
    this.updateLabelWrapForCollapsedState();
  }

  /**
   * Push field-height/compact to the trigger-field element
   * @param {string} val the new field height setting
   */
  onFieldHeightChange(val: string) {
    if (val) {
      const attr = val === 'compact' ? { name: 'compact', val: '' } : { name: 'field-height', val };
      this.triggerField?.setAttribute(attr.name, attr.val);
    } else {
      this.triggerField?.removeAttribute('compact');
      this.triggerField?.removeAttribute('field-height');
    }
  }

  /**
   * Optional callback that can be used to adjust the Popup's placement
   * after all internal adjustments are made.
   * @param {DOMRect} popupRect a Rect object representing the current state of the popup.
   * @returns {object} an adjusted Rect object with "nudged" coordinates.
   */
  onPlace(popupRect: DOMRect): DOMRect {
    return popupRect;
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    const root = (this.getRootNode() as any).host;
    const colorVariant = this.colorVariant ? ` color-variant="${this.colorVariant}"` : '';
    const fieldHeight = this.fieldHeight ? ` field-height="${this.fieldHeight}"` : '';
    const labelState = this.labelState ? ` label-state="${this.labelState}"` : '';
    const labelPosition = this.labelPosition ? ` label-position="${this.labelPosition}"` : '';
    const labelWidth = this.labelWidth ? ` label-width="${this.labelWidth}"` : '';
    const labelAlignY = this.labelAlignY ? ` label-align-y="${this.labelAlignY}"` : '';
    const labelAlignment = this.labelAlignment ? ` label-alignment="${this.labelAlignment}"` : '';
    const labelWrap = this.labelWrap ? ` label-wrap="${this.labelWrap}"` : '';
    const compact = this.compact ? ' compact' : '';
    const noMargins = this.noMargins ? ' no-margins' : '';
    const overflow = this.overflow ? ` overflow="${this.overflow}"` : '';
    const tooltip = this.tooltip ? ` tooltip="${this.tooltip}"` : '';
    const strPressDown = this.localeAPI?.translate?.('PressDown') || 'Press Down arrow to select';
    const classAttr = buildClassAttrib(
      'ids-date-picker',
      `${root?.tagName.includes('CALENDAR') ? 'event-picker' : ''}`
    );

    return `
      <div ${classAttr} part="container">
        <ids-trigger-field
          part="trigger-field"
          ${this.id ? `id="${this.id}"` : ''}
          ${this.label ? `label="${this.label}"` : ''}
          placeholder="${this.placeholder}"
          size="${this.size}"
          format="${this.format}"
          ${this.validate ? `validate="${this.validate}"` : ''}
          validation-events="${this.validationEvents}"
          value="${this.value}"
          ${this.disabled ? `disabled="${this.disabled}"` : ''}
          ${this.readonly ? `readonly="${this.readonly}"` : ''}
          ${this.dirtyTracker ? `dirty-tracker="${this.dirtyTracker}"` : ''}
          ${this.labelBreak ? ` label-break="${this.labelBreak}"` : ''}
          tabbable="${this.tabbable}"
          ${colorVariant}${fieldHeight}${compact}${noMargins}${labelState}${labelPosition}${labelWidth}${labelAlignY}${labelAlignment}${labelWrap}${overflow}${tooltip}
        >
          <span slot="label-post" class="audible">, ${strPressDown}</span>
          <ids-trigger-button
            id="${this.id ? `${this.id}-` : ''}trigger-btn-internal"
            slot="trigger-end" part="trigger-button">
            <ids-text audible="true" translate-text="true">DatePickerTriggerButton</ids-text>
            <ids-icon part="icon" icon="schedule"></ids-icon>
          </ids-trigger-button>
        </ids-trigger-field>

      </div>
    `;
  }

  /**
   * Callback for dirty tracker setting change
   * @param {boolean} value The changed value
   * @returns {void}
   */
  onDirtyTrackerChange(value: boolean) {
    if (value) {
      this.triggerField?.setAttribute(attributes.DIRTY_TRACKER, value);
    } else {
      this.triggerField?.removeAttribute(attributes.DIRTY_TRACKER);
    }
  }

  /**
   * Get reference to the IdsPopup component
   * @returns {IdsDatePickerPopup | undefined | null} the popup component
   */
  get popup(): IdsDatePickerPopup | undefined | null {
    return this.picker;
  }

  /**
   * Create the popup element lazily
   * @private
   */
  #createPickerPopup() {
    const popup = document.createElement('ids-date-picker-popup');
    popup.id = `${this.id ? `${this.id}-` : ''}popup-internal`;
    if (this.showClear) popup.setAttribute('show-clear', String(this.showClear));
    if (this.showCancel) popup.setAttribute('show-cancel', String(this.showCancel));
    if (this.showApply) popup.setAttribute('show-apply', String(this.showApply));
    if (this.showToday) popup.setAttribute('show-today', String(this.showToday));
    popup.setAttribute('show-week-numbers', String(this.showWeekNumbers));
    popup.setAttribute('first-day-of-week', String(this.firstDayOfWeek));
    popup.setAttribute('year', String(this.year));
    popup.setAttribute('month', String(this.month));
    popup.setAttribute('day', String(this.day));
    popup.setAttribute('use-range', String(this.useRange));
    popup.setAttribute('use-utc', String(this.useUtc));
    popup.setAttribute('minute-interval', String(this.minuteInterval));
    popup.setAttribute('second-interval', String(this.secondInterval));
    popup.setAttribute('use-current-time', String(this.useCurrentTime));
    popup.setAttribute('show-picklist-month', String(this.showPicklistMonth));
    popup.setAttribute('show-picklist-week', String(this.showPicklistWeek));
    popup.setAttribute('format', this.format);

    this.container?.appendChild(popup);
  }

  /**
   * Set config on the picker
   * @private
   */
  #configurePickerPopup() {
    this.picker = this.container?.querySelector<IdsDatePickerPopup>('ids-date-picker-popup');

    if (this.picker && this.picker.isConnected && this.picker.appendToTargetParent) {
      this.picker.appendToTargetParent();

      if (this.legend) {
        this.picker.legend = this.legend;
      }

      if (this.disableSettings) {
        this.picker.disableSettings = this.disableSettings;
      }

      const triggerBtnId = `#${this.triggerButton.getAttribute(attributes.ID)}`;
      this.picker.setAttribute(attributes.TRIGGER_TYPE, 'click');
      this.picker.setAttribute(attributes.TARGET, triggerBtnId);
      this.picker.setAttribute(attributes.TRIGGER_ELEM, triggerBtnId);

      // Configure range settings if using range mode
      if (this.useRange && this.rangeSettings) {
        this.picker.useRange = this.useRange;
        this.picker.rangeSettings = this.rangeSettings;
        this.picker.setRangeSettings(this.rangeSettings);
        this.picker.setUseRangeSettings(this.useRange);
      }

      // Configure inner IdsPopup
      if (this.picker.popup) {
        this.picker.popup.setAttribute(attributes.ARROW_TARGET, triggerBtnId);

        // In some cases switch alignment for RTL purposes
        if (this.localeAPI && this.localeAPI.isRTL) {
          const rtlAdjustedAlignValue = `${this.localeAPI.isRTL() || ['lg', 'full'].includes(this.size) ? 'right' : 'left'}`;
          this.picker.popup.setAttribute(attributes.ALIGN, `bottom, ${rtlAdjustedAlignValue}`);
        }

        this.picker.popup.onOutsideClick = this.onOutsideClick.bind(this);

        // Detect switch of X/Y values due to alignment settings,
        // and account for extra width needed to be displayed outside of IdsDatePicker fields
        this.picker.popup.onXYSwitch = onPickerPopupXYSwitch;
      }

      const isInModal = getClosest(this, 'ids-modal');
      const isInActionPanel = getClosest(this, 'ids-action-panel');
      if (isInModal || isInActionPanel) {
        this.picker?.setAttribute(attributes.POSITION_STYLE, 'fixed');
        this.picker?.configurePopup();
        // Ensure trigger button shows full date picker interface in modal/action panel
        if (this.triggerButton) {
          this.triggerButton.setAttribute('show-picklist-month', 'true');
          this.triggerButton.setAttribute('show-picklist-year', 'true');
        }
      }

      this.picker.refreshTriggerEvents();

      if (this.triggerField) {
        this.picker.format = this.format;
        this.picker.value = this.triggerField.value;

        if (this.useRange && this.value) {
          this.#applyRangeSettings();
        }
      }
    }
  }

  /**
   * Handle clicks outside the date picker popup
   * @param {Event} e - The click event
   */
  onOutsideClick(e: Event) {
    if (this.picker) {
      if (!e.composedPath()?.includes(this.picker)) {
        this.triggerEvent('outsideclick.datepicker', this);
        this.#togglePopup(false);
      }
    }
  }

  /**
   * Establish internal event handlers
   * @returns {object} The object for chaining
   */
  #attachEventHandlers(): object {
    // Trigger button click to open/close popup
    this.offEvent('click.date-trigger-button');
    this.onEvent('click.date-trigger-button', this.triggerButton, () => {
      if (this.disabled || this.readonly) return;
      if (!this.input.value || this.input.value.length === 0) {
        this.picker?.clear();
        this.picker?.resetPopupToToday(true);
      }
      this.#togglePopup(!this.picker?.popup?.visible);
    });

    // Date Picker Popup's `dayselected` event causes the trigger field value to the change
    this.offEvent('dayselected.date-picker-popup');
    this.onEvent('dayselected.date-picker-popup', this.container, (e: IdsDayselectedEvent) => {
      if (this.showApply && e.detail.isClicked) {
        return;
      }

      this.applyValue(e.detail.value);
    });

    this.offEvent('applyclicked.date-picker-popup');
    this.onEvent('applyclicked.date-picker-popup', this.container, (e: IdsDayselectedEvent) => {
      if (this.showApply) this.applyValue(e.detail.value);
    });

    // Date Picker Popup's `hide` event can cause the field to become focused
    this.offEvent('hide.date-picker-popup');
    this.onEvent('hide.date-picker-popup', this.picker, (e: CustomEvent) => {
      e.stopPropagation();
      if (e.detail.doFocus) {
        this.triggerField?.focus();
      }
      this.#triggerSelectedEvent();
    });

    // Closes popup on input focus
    this.offEvent('focus.date-picker-input');
    this.onEvent('focus.date-picker-input', this.triggerField, () => {
      this.#togglePopup(false);
    });

    // Input value change
    this.offEvent('change.date-picker-input');
    this.onEvent('change.date-picker-input', this.triggerField, (e) => {
      // If mask is disabled, preserve the original input value without filtering
      if (!this.mask) {
        this.setAttribute(attributes.VALUE, e.detail.value);
        if (this.validate && this.triggerField?.checkValidation) {
          requestAnimationFrame(() => {
            this.triggerField.checkValidation();
          });
        }
        return;
      }

      const valueParts = String(e.detail.value).split(this.rangeSettings.separator);

      const [start, end] = valueParts.map((value: string) => {
        const date = parseLocalDate(value, this.format) ?? new Date(value);
        if (!isValidDate(date)) return '';

        const parsedDate = this.localeAPI.parseDate(
          date?.toISOString() || value,
          { pattern: this.format }
        ) as Date;
        return parsedDate;
      });

      if (this.useRange && this.picker && isValidDate(start) && isValidDate(end)) {
        this.picker.rangeSettings = {
          ...this.picker.rangeSettings,
          start,
          end: start > end ? start : end,
        };

        this.setAttribute(attributes.VALUE, e.detail.value);
      } else if (!this.useRange && isValidDate(this.getDateValue(new Date(e.detail.value)))) {
        this.setAttribute(attributes.VALUE, e.detail.value);
      } else if (e.detail.value === '') {
        // Allow the value to empty to date picker
        this.setAttribute(attributes.VALUE, e.detail.value);
      }
    });

    return this;
  }

  /**
   * Apply the selected date value to the date picker and trigger field
   * @param {Date | null} date - The date to apply (currently unused)
   */
  applyValue(date: Date) {
    this.setAttribute(attributes.VALUE, String(date));
    this.parseEventDate(date);
    this.triggerField?.setAttribute(attributes.VALUE, date);
    this.triggerField?.checkValidation?.();
  }

  /**
   * Handle locale changes by updating format and settings
   */
  onLocaleChange = () => {
    requestAnimationFrame(() => {
      if (this.picker) {
        this.picker.format = this.format;
      }

      const wasDirty = this.triggerField?.isDirty;

      if (wasDirty === false && this.triggerField?.isDirty === true) {
        this.triggerField.resetDirtyTracker();
      }

      this.#applyMask();
      this.#applyRangeSettings();
      this.triggerField.format = this.format;

      // Locale change first day of week only if it's not set as attribute
      if (this.firstDayOfWeek === null) {
        this.firstDayOfWeek = this.localeAPI?.calendar().firstDayofWeek || 0;
      }
    });
  };

  /**
   * Handle language changes by updating validation
   */
  onLanguageChange = () => {
    this.#setAvailableDateValidation();
  };

  /**
   * Establish Internal Keyboard shortcuts
   * @returns {IdsDatePicker} this class-instance object for chaining
   */
  #attachKeyboardListeners(): IdsDatePicker {
    this.offEvent('keydown.date-picker-keyboard');
    this.onEvent('keydown.date-picker-keyboard', this, (e: KeyboardEvent) => {
      this.#handleKeyDownEvent(e);
    });

    return this;
  }

  /**
   * Add picker popup if not loaded yet
   */
  createPickerPopup() {
    if (!this.picker) {
      this.#createPickerPopup();
      this.picker = this.container?.querySelector<IdsDatePickerPopup>('ids-date-picker-popup');
      this.#configurePickerPopup();
    }
  }

  /**
   * Open/close popup with month view
   * @param {boolean} isOpen should be opened or closed
   */
  #togglePopup(isOpen: boolean) {
    if (isOpen && !this.readonly && !this.disabled) {
      // Create popup only when opening
      this.createPickerPopup();
      this.#parseInputDate();

      if (this.value) {
        this.picker?.setAttribute('value', this.value);
      }
      if (this.picker) {
        this.picker.showWeekNumbers = this.showWeekNumbers;
        this.picker.show();
      }

      this.container?.classList.add('is-open');
      this.picker?.focus();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.picker?.hide();

      this.container?.classList.remove('is-open');
    }
  }

  /**
   * Defers `activeDate` to the inner IdsDatePickerPopup's active date,
   * otherwise fallback to the one generated by this one's date props
   * @returns {Date} activeDate from the inner monthView component
   */
  getActiveDate() {
    return this.picker?.getActiveDate() || this.activeDate;
  }

  /**
   * Trigger selected event with current params
   * @returns {void}
   */
  #triggerSelectedEvent(): void {
    const targetDate = this.getActiveDate();

    const args = {
      detail: {
        elem: this,
        date: targetDate,
        useRange: this.useRange,
        rangeStart: this.useRange && (this.picker?.rangeSettings.start)
          ? new Date(this.picker?.rangeSettings.start) : null,
        rangeEnd: this.useRange && (this.picker?.rangeSettings.end)
          ? new Date(this.picker?.rangeSettings.end) : null,
        value: this.useRange ? this.value : this.getFormattedDate(targetDate || new Date())
      }
    };
    this.triggerEvent('dayselected', this, args);
  }

  /**
   * Keyboard events handler
   * @param {KeyboardEvent} e keyboard event
   */
  #handleKeyDownEvent(e: KeyboardEvent): void {
    const key = e.key;
    const stopEvent = () => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
    };

    // Arrow Down opens calendar popup
    if (key === 'ArrowDown' && !this.picker?.popup?.visible) {
      stopEvent();
      this.#togglePopup(true);
    }

    // Escape closes calendar popup
    if (key === 'Escape') {
      stopEvent();
      this.#togglePopup(false);
      this.focus();
    }

    // Hit to for today
    if (this.todayShortcut && key === 't') {
      this.popup?.handleTodayEvent();
      stopEvent();
    }
  }

  /**
   * Parse date from value and pass as year/month/day params what triggers month view to rerender
   */
  #parseInputDate() {
    const parsedDate = this.localeAPI.parseDate(
      this.triggerField?.value,
      { dateFormat: this.format }
    );
    const inputDate = this.localeAPI.isIslamic() ? (parsedDate && umalquraToGregorian(
      (parsedDate as number[])[0],
      (parsedDate as number[])[1],
      (parsedDate as number[])[2]
    )) : parsedDate;
    const setDateParams = (date: Date) => {
      const month = date.getMonth();
      const year = date.getFullYear();
      const day = date.getDate();

      if (this.year !== year) {
        this.year = year;
      }

      if (this.month !== month) {
        this.month = month;
      }

      if (this.day !== day) {
        this.day = day;
      }
    };

    if (!this.useRange) {
      setDateParams((inputDate as Date) || new Date());

      return;
    }

    const rangeParts: Array<string> = this.triggerField.value?.split(this.rangeSettings.separator) || [];
    let rangeStart = rangeParts[0] ? this.localeAPI.parseDate(
      rangeParts[0],
      { dateFormat: this.format }
    ) : null;
    let rangeEnd = rangeParts[1] ? this.localeAPI.parseDate(
      rangeParts[1],
      { dateFormat: this.format }
    ) : null;

    if (this.localeAPI.isIslamic()) {
      rangeStart = rangeStart && Array.isArray(rangeStart) && rangeStart.length >= 3
        ? umalquraToGregorian(rangeStart[0], rangeStart[1], rangeStart[2])
        : null;
      rangeEnd = rangeEnd && Array.isArray(rangeEnd) && rangeEnd.length >= 3
        ? umalquraToGregorian(rangeEnd[0], rangeEnd[1], rangeEnd[2])
        : null;
    }

    if (this.picker && rangeStart && rangeEnd) {
      this.picker.rangeSettings = {
        ...this.picker.rangeSettings,
        start: rangeStart,
        end: rangeEnd
      };
      this.picker.setRangeSettings(this.picker.rangeSettings);
    }

    setDateParams((rangeStart as Date) ?? new Date());
  }

  /**
   * Get an object containing start and end Date objects matching the field's current date-range
   * @returns {{start: Date | undefined, end: Date | undefined}} object containing start and end Date objects
   */
  get dateRange(): { start: Date | undefined, end: Date | undefined } {
    const prevFormat = this.triggerField.format;
    const prevDelimeter = this.triggerField.maskOptions.delimiter;
    const rangeParts = String(this.value ?? '').trim().split(prevDelimeter || this.rangeSettings.separator);
    const wasPreviousIslamic = this.localeAPI.isIslamic(this.localeAPI.previousLocale);

    const [start, end] = rangeParts.map((value: string) => this.#parseDateWithLocale(value, prevFormat, wasPreviousIslamic));

    return { start, end };
  }

  /**
   * Takes a date string value (presumably passed from an event) and converts
   * the value to day/month/year attributes
   * @param {Date} val stringified date
   */
  parseEventDate(val: Date) {
    if (!val || typeof val !== 'string') return;

    const date = new Date(val);
    if (!isValidDate(date)) return;

    const month = date.getMonth();
    const year = date.getFullYear();
    const day = date.getDate();

    if (this.year !== year) {
      this.year = year;
    }

    if (this.month !== month) {
      this.month = month;
    }

    if (this.day !== day) {
      this.day = day;
    }
  }

  /**
   * Applying ids-mask to the input when changing locale or format
   */
  #applyMask() {
    if (this.triggerField && this.mask) {
      this.triggerField.mask = this.#getMaskType();
      this.triggerField.maskOptions = { format: this.format, delimiter: this.rangeSettings.separator };

      // Temporarily disable dirty tracker events to prevent false dirty state
      // when reformatting date for locale changes
      const wasDirtyTrackerEnabled = this.triggerField.dirtyTracker;
      if (wasDirtyTrackerEnabled) {
        this.triggerField.dirtyTrackerEvents('remove');
      }

      let formattedValue: string;
      if (this.useRange && this.value) {
        const { start, end } = this.dateRange;
        formattedValue = [
          this.getFormattedDate(start ?? ''),
          this.rangeSettings.separator,
          this.getFormattedDate(end ?? '')
        ].join('');
      } else {
        formattedValue = this.getFormattedDate(this.dateValue ?? '').trim();
      }

      this.triggerField.mask = null;
      this.triggerField.value = formattedValue;
      this.value = formattedValue;
      this.triggerField.mask = this.#getMaskType();

      // Re-enable dirty tracker events after value update
      if (wasDirtyTrackerEnabled) {
        this.triggerField.dirtyTrackerEvents();
      }
    }
  }

  /**
   * Available date validation extend validation mixin
   * Uses month view to define if date is available
   */
  #setAvailableDateValidation(): void {
    if (this.validate?.includes('availableDate')) {
      this.triggerField?.addValidationRule({
        id: 'availableDate',
        type: 'error',
        message: this.localeAPI?.translate('UnavailableDate'),
        messageId: 'UnavailableDate',
        check: (input: any) => {
          if (!input.value) return true;

          const date = this.localeAPI.parseDate(
            input.value,
            this.format
          ) as Date;

          return isValidDate(date) && !this.picker?.isDisabledByDate(date);
        }
      });
    }
  }

  /**
   * Get the appropriate mask type based on range usage
   * @returns {string} mask type
   */
  #getMaskType(): string {
    return this.useRange ? 'rangeDate' : 'date';
  }

  /**
   * Focuses input or dropdown
   * @returns {void}
   */
  focus(): void {
    this.triggerField?.focus();
    this.container?.querySelector<IdsToggleButton>('ids-toggle-button')?.container?.focus();
  }

  /**
   * Public method to open calendar popup
   * @returns {void}
   */
  open(): void {
    this.#togglePopup(true);
  }

  /**
   * Public method to close calendar popup
   * @returns {void}
   */
  close(): void {
    this.#togglePopup(false);
  }

  /**
   * Defines if the format has hours/minutes/seconds pattern to show time picker
   * @returns {boolean} whether or not to show time picker
   */
  hasTime(): boolean {
    return this.format?.includes('h') || this.format?.includes('m') || this.format?.includes('s');
  }

  /**
   * Helper to set the date with time from time picker
   * @param {any} val date to add time values
   * @returns {Date} date with time values
   */
  #setTime(val: any): Date {
    const date = isValidDate(val) ? val : new Date(val);
    const timePicker = this.container?.querySelector<IdsTimePicker>('ids-time-picker');

    if (!this.hasTime() || !timePicker) return date;

    const hours: number = timePicker.hours;
    const minutes: number = timePicker.minutes;
    const seconds: number = timePicker.seconds;
    const period: string = timePicker.period;
    const dayPeriodIndex = this.localeAPI?.calendar().dayPeriods?.indexOf(period);

    date.setHours(hoursTo24(hours, dayPeriodIndex), minutes, seconds);

    return date;
  }

  /**
   * Handle changes to the id attribute
   * @param {string|null} value - The new id value
   */
  onIdChange(value: string | null) {
    if (value) {
      this.triggerField!.id = `${value}-trigger-field`;
      if (this.picker) this.picker.id = `${value}-popup-internal`;
      if (this.triggerButton) this.triggerButton.id = `${value}-trigger-btn-internal`;
    } else {
      this.removeAttribute(attributes.ID);
      if (this.triggerField) this.triggerField.removeAttribute('id');
      if (this.picker) this.picker.removeAttribute('id');
      if (this.triggerButton) this.triggerButton.removeAttribute('id');
    }
    this.#configurePickerPopup();
  }

  /**
   * Check if input, dropdown or the calendar toolbar has focus
   * @returns {boolean} whether or not an element has focus
   */
  get hasFocus(): boolean {
    const input = this.triggerField?.container.querySelector('input');
    const dropdown = this.container?.querySelector('.dropdown-btn')?.shadowRoot?.querySelector('button');

    return input?.matches(':focus') || dropdown?.matches(':focus');
  }

  /**
   * Get the value attribute
   * @returns {string} the value param
   */
  get value(): string {
    return this.getAttribute(attributes.VALUE) ?? '';
  }

  /**
   * Set input value. Should parse a date from the value
   * Set dropdown button text if the component is dropdown
   * Set text if the component is used in calendar toolbar
   * @param {string|null} val value param
   */
  set value(val: string | null) {
    let finalVal: string = (val == null || val === 'null') ? '' : val;

    let dateValue = this.getDateValue(finalVal);

    if (!dateValue) {
      const date = new Date(finalVal || '');
      dateValue = isValidDate(date) ? this.getDateValue(date.toISOString()) : null;
    }

    // Restore previous value if range dates are invalid or violate minDays/maxDays
    if (this.useRange && finalVal && (!this.#hasValidRangeDates(finalVal) || this.#isRangeOutOfBounds(finalVal))) {
      finalVal = this.#lastValidRangeValue ?? '';
      dateValue = this.getDateValue(finalVal);
    }

    this.dateValue = dateValue;

    const formattedVal = this.useUtc && finalVal ? this.getFormattedDate(finalVal) : finalVal;

    this.triggerField?.setAttribute(attributes.VALUE, formattedVal);
    this.picker?.setAttribute(attributes.VALUE, finalVal);
    this.setAttribute(attributes.VALUE, finalVal);

    this.#applyRangeSettings();

    if (this.validateOnValueSet && this.validate && this.triggerField?.checkValidation && !this.suppressValidation) {
      const shouldValidateEmpty = finalVal === '' && (!this.#isInitializing);
      if ((finalVal && finalVal.trim() !== '') || shouldValidateEmpty) {
        requestAnimationFrame(() => {
          this.triggerField.checkValidation();
        });
      }
    }

    if (this.useRange) this.#lastValidRangeValue = finalVal;
  }

  /**
   * Check if a range value string contains two valid parseable dates
   * @private
   * @param {string} val the range value string
   * @returns {boolean} true if both dates are valid
   */
  #hasValidRangeDates(val: string): boolean {
    const rangeParts = String(val).split(this.rangeSettings.separator);

    // collapseSameDates: single date value is valid for same-day ranges
    if (rangeParts.length === 1 && this.rangeSettings.collapseSameDates) {
      const parsed = this.localeAPI.parseDate(rangeParts[0].trim(), { dateFormat: this.format });
      if (this.localeAPI.isIslamic() && Array.isArray(parsed) && parsed.length >= 3) {
        const converted = umalquraToGregorian(parsed[0], parsed[1], parsed[2]);
        return converted instanceof Date && isValidDate(converted);
      }
      return isValidDate(parsed as Date);
    }

    if (rangeParts.length !== 2) return false;

    return rangeParts.every((part: string) => {
      const parsed = this.localeAPI.parseDate(part.trim(), { dateFormat: this.format });
      if (this.localeAPI.isIslamic() && Array.isArray(parsed) && parsed.length >= 3) {
        const converted = umalquraToGregorian(parsed[0], parsed[1], parsed[2]);
        return converted instanceof Date && isValidDate(converted);
      }
      return isValidDate(parsed as Date);
    });
  }

  /**
   * Check if a range value violates minDays/maxDays constraints
   * @private
   * @param {string} val the range value string
   * @returns {boolean} true if the range is out of bounds
   */
  #isRangeOutOfBounds(val: string): boolean {
    const { minDays, maxDays } = this.rangeSettings;
    if (!minDays && !maxDays) return false;

    const rangeParts = String(val).split(this.rangeSettings.separator);

    // collapseSameDates: single date = same-day range (1 day inclusive)
    if (rangeParts.length === 1 && this.rangeSettings.collapseSameDates) {
      if (minDays && minDays > 1) return true;
      return false;
    }

    if (rangeParts.length !== 2) return false;

    const [startParsed, endParsed] = rangeParts.map((part: string) => {
      const parsed = this.localeAPI.parseDate(part.trim(), { dateFormat: this.format });
      if (this.localeAPI.isIslamic() && Array.isArray(parsed) && parsed.length >= 3) {
        const converted = umalquraToGregorian(parsed[0], parsed[1], parsed[2]);
        return converted instanceof Date ? converted : new Date(NaN);
      }
      return parsed as Date;
    });

    const daysDiff = Math.abs(Math.ceil((endParsed.getTime() - startParsed.getTime()) / (1000 * 60 * 60 * 24)));
    if (maxDays && maxDays > 0 && daysDiff > maxDays) return true;
    if (minDays && minDays > 0 && daysDiff < minDays) return true;

    return false;
  }

  /**
   * Get the utcDateValue property for Date objects and ISO strings
   * @returns {Date | null} the date value
   */
  get utcDateValue(): Date | null {
    const val = this.getAttribute(attributes.UTC_DATE_VALUE);
    if (!val) return null;
    const date = new Date(val);
    return isValidDate(date) ? date : null;
  }

  /**
   * Set utcDateValue for Date objects and ISO strings with UTC conversion
   * @param {Date | string | null} val Date object, ISO string, or null
   */
  set utcDateValue(val: Date | string | null) {
    if (!val) {
      this.removeAttribute(attributes.UTC_DATE_VALUE);
      this.value = '';
      return;
    }
    const date = val instanceof Date ? val : new Date(val);
    if (!isValidDate(date)) return;

    this.setAttribute(attributes.UTC_DATE_VALUE, date.toISOString());
    this.value = this.getFormattedDate(date.toISOString());
  }

  /**
   * Get the placeholder attribute
   * @returns {string} the placeholder param
   */
  get placeholder(): string {
    const boolVal = stringToBool(this.getAttribute(attributes.PLACEHOLDER));

    return boolVal ? this.format : '';
  }

  /**
   * Set input placeholder
   * @param {boolean|string|null} val of placeholder to be set
   */
  set placeholder(val: boolean | string | null) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.PLACEHOLDER, this.placeholder);
      this.triggerField?.setAttribute(attributes.PLACEHOLDER, this.placeholder);
    } else {
      this.removeAttribute(attributes.PLACEHOLDER);
      this.triggerField?.removeAttribute(attributes.PLACEHOLDER);
    }
  }

  /**
   * Get the disabled attribute
   * @returns {boolean} the disabled param
   */
  get disabled(): boolean {
    const attrVal = this.getAttribute(attributes.DISABLED);

    return stringToBool(attrVal);
  }

  /**
   * Set trigger field disabled attribute
   * @param {boolean} val disabled param value
   */
  set disabled(val: boolean) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.DISABLED, 'true');
      this.triggerField?.setAttribute(attributes.DISABLED, boolVal);
      // disabled and readonly are mutually exclusive — remove readonly when disabling
      if (this.hasAttribute(attributes.READONLY)) {
        this.removeAttribute(attributes.READONLY);
        this.triggerField?.removeAttribute(attributes.READONLY);
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.triggerField?.removeAttribute(attributes.DISABLED);
    }
  }

  /**
   * Get the readonly attribute
   * @returns {boolean} the readonly param
   */
  get readonly(): boolean {
    const attrVal = this.getAttribute(attributes.READONLY);

    return stringToBool(attrVal);
  }

  /**
   * Set trigger field readonly attribute
   * @param {boolean} val readonly param value
   */
  set readonly(val: boolean) {
    const boolVal: boolean = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.READONLY, 'true');
      this.triggerField?.setAttribute(attributes.READONLY, 'true');
      // disabled and readonly are mutually exclusive — remove disabled when setting readonly
      if (this.hasAttribute(attributes.DISABLED)) {
        this.removeAttribute(attributes.DISABLED);
        this.triggerField?.removeAttribute(attributes.DISABLED);
      }
    } else {
      this.removeAttribute(attributes.READONLY);
      this.triggerField?.removeAttribute(attributes.READONLY);
    }
  }

  /**
   * Get the size attribute (default is sm)
   * @returns {string} the size param
   */
  get size(): string { return this.getAttribute(attributes.SIZE) ?? 'sm'; }

  /**
   * Set the size (width) of input
   * @param {string|null} val [xs, sm, mm, md, lg, full]
   */
  set size(val: string | null) {
    if (val) {
      this.setAttribute(attributes.SIZE, val);
      this.triggerField?.setAttribute(attributes.SIZE, val);
      if (val === 'full') this.container?.classList.add('full');
    } else {
      this.removeAttribute(attributes.SIZE);
      this.triggerField?.setAttribute(attributes.SIZE, 'sm');
      this.container?.classList.remove('full');
    }
  }

  /**
   * Get the tabbable attribute
   * @returns {boolean} the tabbable param (not tabbable by default)
   */
  get tabbable(): boolean {
    const attrVal = this.getAttribute(attributes.TABBABLE);
    return attrVal !== null ? stringToBool(attrVal) : false;
  }

  /**
   * Set trigger field tabbable attribute
   * @param {boolean} val true of false depending if the trigger field is tabbable
   */
  set tabbable(val: boolean) {
    const isTabbable = stringToBool(val);
    if (isTabbable) {
      this.setAttribute(attributes.TABBABLE, String(isTabbable));
      this.triggerField?.setAttribute(attributes.TABBABLE, String(isTabbable));
      this.triggerButton?.setAttribute(attributes.TABBABLE, String(isTabbable));
    } else {
      this.removeAttribute(attributes.TABBABLE);
      this.triggerField?.removeAttribute(attributes.TABBABLE);
      this.triggerButton?.removeAttribute(attributes.TABBABLE);
    }
  }

  /**
   * Get the validate attribute
   * @returns {string | null} the validate param
   */
  get validate(): string | null { return this.getAttribute(attributes.VALIDATE); }

  /**
   * Set trigger field/input validation
   * @param {string|null} val validate param
   */
  set validate(val: string | null) {
    if (val) {
      this.setAttribute(attributes.VALIDATE, val);
      this.triggerField?.setAttribute(attributes.VALIDATE, val);
      this.triggerField?.setAttribute(attributes.VALIDATION_EVENTS, this.validationEvents);
      this.triggerField?.handleValidation?.();
    } else {
      this.removeAttribute(attributes.VALIDATE);
      this.triggerField?.removeAttribute(attributes.VALIDATE);
      this.triggerField?.removeAttribute(attributes.VALIDATION_EVENTS);
      this.triggerField?.handleValidation?.();
    }

    this.#setAvailableDateValidation();
  }

  /**
   * Get the validation-events attributes
   * @returns {string} the validationEvents param (default is 'change blur')
   */
  get validationEvents(): string { return this.getAttribute(attributes.VALIDATION_EVENTS) ?? 'change blur'; }

  /**
   * Set which input events to fire validation on
   * @param {string|null} val validation-events attribute
   */
  set validationEvents(val: string | null) {
    if (val) {
      this.setAttribute(attributes.VALIDATION_EVENTS, val);
      this.triggerField?.setAttribute(attributes.VALIDATION_EVENTS, val);
    } else {
      this.removeAttribute(attributes.VALIDATION_EVENTS);
      this.triggerField?.removeAttribute(attributes.VALIDATION_EVENTS);
    }
  }

  /**
   * Handle changes to the format attribute
   * @param {string} newValue - The new format value
   */
  onFormatChange(newValue: string) {
    if (newValue) {
      this.picker?.setAttribute(attributes.FORMAT, newValue);
      this.triggerField?.setAttribute(attributes.FORMAT, newValue);
    } else {
      this.picker?.removeAttribute(attributes.FORMAT);
      this.triggerField?.removeAttribute(attributes.FORMAT);
    }

    if (this.placeholder) {
      this.placeholder = this.format;
    }

    this.#applyMask();
  }

  /**
   * Get the show-today attribute
   * @returns {boolean} the showToday param (true by default if no attribute)
   */
  get showToday(): boolean {
    const attrVal = this.getAttribute(attributes.SHOW_TODAY);

    // true by default if no attribute
    return attrVal !== null ? stringToBool(attrVal) : true;
  }

  /**
   * Set whether or not month view today button should be show
   * @param {boolean} val show-today attribute value
   */
  set showToday(val: boolean) {
    this.setAttribute(attributes.SHOW_TODAY, String(val));
    this.picker?.setAttribute(attributes.SHOW_TODAY, String(val));
  }

  /**
   * Sets the margin-block-end spacing token on the inner .ids-date-picker container.
   * Accepts token shorthand keys (e.g. 'sm', 'lg') that map to IDS spacing CSS variables.
   * @param {MarginBlockEndKey | null} value spacing token key, or null to remove
   */
  set marginBlockEnd(value: MarginBlockEndKey | null) {
    if (value && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, value)) {
      this.setAttribute(attributes.MARGIN_BLOCK_END, value);
    } else {
      this.removeAttribute(attributes.MARGIN_BLOCK_END);
      value = null;
    }
    if (this.input) this.input.marginBlockEnd = value;
  }

  /**
   * Gets the margin-block-end token key
   * @returns {MarginBlockEndKey | null} the current spacing token key, or null if not set
   */
  get marginBlockEnd(): MarginBlockEndKey | null {
    const val = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey | null;
    if (val && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, val)) return val;
    return null;
  }

  /**
   * Sets the no margins attribute
   * @param {boolean} value The value for no margins attribute
   */
  set noMargins(value: boolean) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.NO_MARGINS, '');
      this.triggerField?.setAttribute(attributes.NO_MARGINS, '');
      return;
    }
    this.removeAttribute(attributes.NO_MARGINS);
    this.triggerField?.removeAttribute(attributes.NO_MARGINS);
  }

  /**
   * Get the no margins state
   * @returns {boolean} true if margins are disabled
   */
  get noMargins() {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Sets the overflow behavior for text content
   * @param {string | null} value The overflow value (e.g., 'ellipsis')
   */
  set overflow(value: string | null) {
    if (value) {
      this.setAttribute(attributes.OVERFLOW, value);
      this.triggerField?.setAttribute(attributes.OVERFLOW, value);
    } else {
      this.removeAttribute(attributes.OVERFLOW);
      this.triggerField?.removeAttribute(attributes.OVERFLOW);
    }
  }

  /**
   * Get the overflow behavior
   * @returns {string | null} the overflow value
   */
  get overflow(): string | null {
    return this.getAttribute(attributes.OVERFLOW);
  }

  /**
   * Sets the tooltip text or behavior
   * @param {string | boolean | null} value The tooltip text
   */
  set tooltip(value: string | boolean | null) {
    if (value === true || value === 'true') {
      this.setAttribute(attributes.TOOLTIP, 'true');
      this.triggerField?.setAttribute(attributes.TOOLTIP, 'true');
    } else if (value && typeof value === 'string') {
      this.setAttribute(attributes.TOOLTIP, value);
      this.triggerField?.setAttribute(attributes.TOOLTIP, value);
    } else {
      this.removeAttribute(attributes.TOOLTIP);
      this.triggerField?.removeAttribute(attributes.TOOLTIP);
    }
  }

  /**
   * Get the tooltip text or behavior
   * @returns {string} the tooltip value
   */
  get tooltip(): string {
    return this.getAttribute(attributes.TOOLTIP) || '';
  }

  /**
   * Handle changes to the first day of week setting
   * @param {number} newValue - The new first day of week value (0-6, where 0 is Sunday)
   */
  onFirstDayOfWeekChange(newValue: number) {
    this.picker?.setAttribute(attributes.FIRST_DAY_OF_WEEK, String(newValue));
  }

  /**
   * Handle changes to the month attribute
   * @param {number} newValue - The new month value
   * @param {boolean} isValid - Whether the month value is valid
   */
  onMonthChange(newValue: number, isValid: boolean) {
    if (isValid) {
      this.picker?.setAttribute(attributes.MONTH, String(newValue));
    } else {
      this.picker?.removeAttribute(attributes.MONTH);
    }
  }

  /**
   * Handle changes to the year attribute
   * @param {number} newValue - The new year value
   * @param {boolean} isValid - Whether the year value is valid
   */
  onYearChange(newValue: number, isValid: boolean) {
    if (isValid) {
      this.picker?.setAttribute(attributes.YEAR, String(newValue));
    } else {
      this.picker?.removeAttribute(attributes.YEAR);
    }
  }

  /**
   * Handle changes to the day attribute
   * @param {number} newValue - The new day value
   * @param {boolean} isValid - Whether the day value is valid
   */
  onDayChange(newValue: number, isValid: boolean) {
    if (isValid) {
      this.picker?.setAttribute(attributes.DAY, String(newValue));
    } else {
      this.picker?.removeAttribute(attributes.DAY);
    }
  }

  /**
   * Get reference to the IdsTriggerField
   * @returns {any} the trigger field element
   */
  get input() {
    return this.triggerField;
  }

  /**
   * Handle changes to the disable settings
   * @param {IdsDisableSettings} val - The new disable settings
   */
  onDisableSettingsChange(val: IdsDisableSettings) {
    if (this.picker) this.picker.disableSettings = val;
  }

  /**
   * Handle changes to the legend settings
   * @param {Array<IdsLegend>} val - The new legend settings array
   */
  onLegendSettingsChange(val: Array<IdsLegend>) {
    if (this.picker) this.picker.legend = val;
  }

  /**
   * Handle changes to the range settings
   * @param {IdsRangeSettings} val - The new range settings
   */
  onRangeSettingsChange(val: IdsRangeSettings) {
    if (this.picker) this.picker.rangeSettings = val;

    if (val?.start && val?.end) {
      const startDate = this.localeAPI.formatDate(this.#setTime(val.start), { pattern: this.format });
      const endDate = this.localeAPI.formatDate(this.#setTime(val.end), { pattern: this.format });

      // Check if dates are the same and showSingleDateAsRange is true
      if (this.rangeSettings.collapseSameDates && startDate === endDate) {
        this.value = startDate;
      } else {
        this.value = `${startDate}${this.rangeSettings.separator}${endDate}`;
      }
    }
  }

  /**
   * Handle changes to the use range setting
   * @param {boolean} val - Whether range mode is enabled
   */
  onUseRangeChange(val: boolean) {
    const btnApply = this.container?.querySelector('.popup-btn-apply');
    if (val) {
      this.picker?.setAttribute(attributes.USE_RANGE, String(val));
      btnApply?.removeAttribute('hidden');
      btnApply?.setAttribute('disabled', 'true');
    } else {
      this.picker?.removeAttribute(attributes.USE_RANGE);
      btnApply?.setAttribute('hidden', 'true');
      btnApply?.removeAttribute('disabled');
    }
  }

  /**
   * Get the mask attribute
   * @returns {boolean} true if date mask is enabled
   */
  get mask(): boolean {
    const attrVal = this.getAttribute(attributes.MASK);

    return stringToBool(attrVal);
  }

  /**
   * Enable/disable date mask for the input
   * @param {boolean} val mask param value
   */
  set mask(val: boolean) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.MASK, 'true');
      this.triggerField?.setAttribute(attributes.MASK, this.#getMaskType());
    } else {
      this.removeAttribute(attributes.MASK);
      this.triggerField?.removeAttribute(attributes.MASK);
    }
  }

  /**
   * Get the minute-interval attribute
   * @returns {number} the minuteInterval value
   */
  get minuteInterval(): number {
    return stringToNumber(this.getAttribute(attributes.MINUTE_INTERVAL));
  }

  /**
   * Set interval in minutes dropdown
   * @param {string|number|null} val minute-interval attribute value
   */
  set minuteInterval(val: string | number | null) {
    const numberVal = stringToNumber(val);
    if (numberVal) {
      this.setAttribute(attributes.MINUTE_INTERVAL, String(numberVal));
    } else {
      this.removeAttribute(attributes.MINUTE_INTERVAL);
    }
    if (this.picker) this.picker.minuteInterval = numberVal;
  }

  /**
   * Get the second-interval attribute
   * @returns {number} the secondInterval value
   */
  get secondInterval(): number {
    return stringToNumber(this.getAttribute(attributes.SECOND_INTERVAL));
  }

  /**
   * Set interval in seconds dropdown
   * @param {string|number|null} val second-interval attribute value
   */
  set secondInterval(val: string | number | null) {
    const numberVal = stringToNumber(val);
    if (numberVal) {
      this.setAttribute(attributes.SECOND_INTERVAL, String(numberVal));
    } else {
      this.removeAttribute(attributes.SECOND_INTERVAL);
    }
    if (this.picker) this.picker.secondInterval = numberVal;
  }

  /**
   * Get the show-clear attribute
   * @returns {boolean} the showClear param
   */
  get showClear(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_CLEAR));
  }

  /**
   * Set whether or not to show clear button in the calendar popup
   * @param {boolean} val show-clear attribute value
   */
  set showClear(val: boolean) {
    const boolVal = stringToBool(val);
    if (boolVal) {
      this.setAttribute(attributes.SHOW_CLEAR, 'true');
    } else {
      this.removeAttribute(attributes.SHOW_CLEAR);
    }
    if (this.picker) this.picker.showClear = boolVal;
  }

  /**
   * Get the show-cancel attribute
   * @returns {boolean} the showCancel param
   */
  get showCancel(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_CANCEL));
  }

  /**
   * Set whether or not to show cancel button when the picker is expanded
   * @param {boolean} val show-cancel attribute value
   */
  set showCancel(val: boolean) {
    const boolVal = stringToBool(val);
    if (boolVal) {
      this.setAttribute(attributes.SHOW_CANCEL, 'true');
    } else {
      this.removeAttribute(attributes.SHOW_CANCEL);
    }
    if (this.picker) this.picker.showCancel = boolVal;
  }

  /**
   * Get the show-apply attribute
   * @returns {boolean} the showApply param
   */
  get showApply(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_APPLY));
  }

  /**
   * Set whether or not to show cancel button when the picker is expanded
   * @param {boolean} val show-cancel attribute value
   */
  set showApply(val: boolean) {
    const boolVal = stringToBool(val);
    if (boolVal) {
      this.setAttribute(attributes.SHOW_APPLY, 'true');
    } else {
      this.removeAttribute(attributes.SHOW_APPLY);
    }
    if (this.picker) this.picker.showApply = boolVal;
  }

  /**
   * Get the show-picklist-year attribute (default is true)
   * @returns {boolean} the showPicklistYear param
   */
  get showPicklistYear(): boolean {
    const attrVal = this.getAttribute(attributes.SHOW_PICKLIST_YEAR);
    if (attrVal) {
      return stringToBool(attrVal);
    }

    return true;
  }

  /**
   * Whether or not to show a list of years in the picklist
   * @param {boolean} val value to be set as show-picklist-year attribute converted to boolean
   */
  set showPicklistYear(val: boolean) {
    const boolVal = stringToBool(val);
    this.setAttribute(attributes.SHOW_PICKLIST_YEAR, String(boolVal));
    if (this.picker) this.picker.showPicklistYear = boolVal;
  }

  /**
   * Get the show-picklist-month attribute (default is true)
   * @returns {boolean} the showPicklistMonth param
   */
  get showPicklistMonth(): boolean {
    const attrVal = this.getAttribute(attributes.SHOW_PICKLIST_MONTH);

    if (attrVal) {
      return stringToBool(attrVal);
    }

    return true;
  }

  /**
   * Whether or not to show a list of months in the picklist
   * @param {boolean} val value to be set as show-picklist-month attribute converted to boolean
   */
  set showPicklistMonth(val: boolean) {
    const boolVal = stringToBool(val);
    this.setAttribute(attributes.SHOW_PICKLIST_MONTH, String(boolVal));
    if (this.picker) this.picker.showPicklistMonth = boolVal;
  }

  /**
   * Get the show-picklist-week attribute
   * @returns {boolean} the showPicklistWeek param
   */
  get showPicklistWeek(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_PICKLIST_WEEK));
  }

  /**
   * Whether or not to show week numbers in the picklist
   * @param {boolean} val value to be set as show-picklist-week attribute converted to boolean
   */
  set showPicklistWeek(val: boolean) {
    const boolVal = stringToBool(val);
    this.setAttribute(attributes.SHOW_PICKLIST_WEEK, String(boolVal));
    if (this.picker) this.picker.showPicklistWeek = boolVal;
  }

  /**
   * Get the use-current-time attribute
   * @returns {boolean} the useCurrentTime param
   */
  get useCurrentTime(): boolean {
    return stringToBool(this.getAttribute(attributes.USE_CURRENT_TIME));
  }

  /**
   * Set whether or not to show current time in the time picker
   * @param {boolean} val useCurrentTime param value
   */
  set useCurrentTime(val: boolean) {
    const boolVal = stringToBool(val);

    if (boolVal) {
      this.setAttribute(attributes.USE_CURRENT_TIME, boolVal.toString());
    } else {
      this.removeAttribute(attributes.USE_CURRENT_TIME);
    }

    if (this.picker) this.picker.useCurrentTime = boolVal;
  }

  /**
   * Set whether or not to show week numbers in the calendar view
   * @param {boolean} val If true, shows week numbers in the calendar view
   */
  set showWeekNumbers(val: boolean) {
    const boolVal = stringToBool(val);
    if (boolVal) {
      this.setAttribute(attributes.SHOW_WEEK_NUMBERS, '');
    } else {
      this.removeAttribute(attributes.SHOW_WEEK_NUMBERS);
    }
    if (this.picker) {
      this.picker.showWeekNumbers = boolVal;
    }
  }

  /**
   * Get the show week numbers state
   * @returns {boolean} true if week numbers are shown
   */
  get showWeekNumbers(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_WEEK_NUMBERS));
  }

  /**
   * Set whether or not to enable t as a shortcut key in the datepicker
   * @param {boolean} val If true, enabled today shortcut key
   */
  set todayShortcut(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.TODAY_SHORTCUT, '');
    } else {
      this.removeAttribute(attributes.TODAY_SHORTCUT);
    }
  }

  /**
   * Get the today shortcut state
   * @returns {boolean} true if today shortcut is enabled
   */
  get todayShortcut(): boolean {
    return stringToBool(this.getAttribute(attributes.TODAY_SHORTCUT));
  }

  /**
   * Validates the current date range value when in range mode
   * @returns {boolean} True if the range is valid or if range mode is not enabled
   */
  validateRangeCurrentValue() {
    if (!this.useRange) {
      return true;
    }

    return this.triggerField.rules?.rangeDate?.check(this.triggerField.input);
  }

  #parseDateWithLocale(dateValue: string, prevFormat?: string, wasPreviousIslamic?: boolean): Date | undefined {
    let parsedDate: Date;
    if (this.localeAPI.isIslamic()) {
      parsedDate = parseLocalDate(dateValue, prevFormat || this.format) || new Date();
      if (!parsedDate) return undefined;
    } else {
      // Current locale is not Islamic - use localeAPI.parseDate
      const apiDate = this.localeAPI.parseDate(dateValue, { dateFormat: prevFormat || this.format });
      if (!apiDate) return undefined;

      // If previous locale was Islamic, convert from Islamic to Gregorian
      if (Array.isArray(apiDate) && apiDate.length >= 3) {
        parsedDate = wasPreviousIslamic
          ? umalquraToGregorian(apiDate[0], apiDate[1], apiDate[2])
          : new Date(apiDate[0], apiDate[1], apiDate[2]);
      } else if (wasPreviousIslamic) {
        const date = (apiDate as Date);
        parsedDate = umalquraToGregorian(date.getFullYear(), date.getMonth(), date.getDate());
      } else {
        parsedDate = apiDate as Date;
      }
    }
    return this.localeAPI.parseDate(
      parsedDate.toISOString(),
      { pattern: prevFormat || this.format }
    ) as Date;
  }
}

export default IdsDatePicker;
